import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

const WishlistContext = createContext();

const STORAGE_KEY = 'bg_wishlist_v1';

const readStore = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch { return []; }
};

const writeStore = (arr) => {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(arr)); } catch { /* quota / disabled */ }
};

export const WishlistProvider = ({ children }) => {
  const [items, setItems] = useState(() => readStore());
  const [open, setOpen] = useState(false);

  // Sync across tabs
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY) setItems(readStore());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const persist = useCallback((next) => {
    setItems(next);
    writeStore(next);
  }, []);

  const has = useCallback((id) => items.some(i => i.id === id), [items]);

  const toggle = useCallback((product) => {
    setItems(prev => {
      const exists = prev.some(i => i.id === product.id);
      const next = exists
        ? prev.filter(i => i.id !== product.id)
        : [{
            id: product.id,
            name: product.name,
            strength: product.strength || '',
            packaging: product.packaging || '',
            catLabel: product.catLabel || '',
            price: product.price,
            image: product.image || product.fallbackImage || '',
            fallbackImage: product.fallbackImage || '',
            isRx: !!product.isRx,
            addedAt: Date.now(),
          }, ...prev];
      writeStore(next);
      return next;
    });
  }, []);

  const remove = useCallback((id) => {
    setItems(prev => {
      const next = prev.filter(i => i.id !== id);
      writeStore(next);
      return next;
    });
  }, []);

  const clear = useCallback(() => persist([]), [persist]);

  return (
    <WishlistContext.Provider value={{ items, count: items.length, has, toggle, remove, clear, open, setOpen }}>
      {children}
    </WishlistContext.Provider>
  );
};

export const useWishlist = () => useContext(WishlistContext);
